import { useState, useEffect } from 'react'
import { BarChart3, TrendingUp, Users, MessageSquare, CheckCircle, AlertCircle, Mail, MessageCircle, Bell } from 'lucide-react'
import { announcementApi } from '../services/api'
import api from '../services/api'
import PageHeader from '../components/common/PageHeader'
import LoadingSpinner from '../components/common/LoadingSpinner'

const AnnouncementStats = () => {
  const [globalStats, setGlobalStats] = useState(null)
  const [buildingStats, setBuildingStats] = useState([])
  const [buildings, setBuildings] = useState([])
  const [selectedBuildingId, setSelectedBuildingId] = useState('')
  const [selectedBuildingStats, setSelectedBuildingStats] = useState(null)
  const [loading, setLoading] = useState(true)
  const [buildingStatsLoading, setBuildingStatsLoading] = useState(false)

  useEffect(() => {
    loadInitialData()
  }, [])

  useEffect(() => {
    if (selectedBuildingId) {
      loadBuildingStats(selectedBuildingId)
    } else {
      setSelectedBuildingStats(null)
    }
  }, [selectedBuildingId])

  const loadInitialData = async () => {
    try {
      setLoading(true)
      await Promise.all([
        loadGlobalStats(),
        loadBuildings()
      ])
    } catch (error) {
      console.error('Error loading initial data:', error)
    } finally {
      setLoading(false)
    }
  }

  const loadGlobalStats = async () => {
    try {
      const response = await announcementApi.getGlobalStats()
      setGlobalStats(response.data)
    } catch (error) {
      console.error('Error loading global stats:', error)
    }
  }

  const loadBuildings = async () => {
    try {
      const response = await api.get('/Building')
      const buildingList = response.data.items || []
      setBuildings(buildingList)
      
      // Load stats for each building
      const buildingStatsPromises = buildingList.map(async (building) => {
        try {
          const statsResponse = await announcementApi.getBuildingStats(building.buildingId)
          return {
            buildingId: building.buildingId,
            buildingName: building.name,
            stats: statsResponse.data
          }
        } catch (error) {
          console.error(`Error loading stats for building ${building.buildingId}:`, error)
          return {
            buildingId: building.buildingId,
            buildingName: building.name,
            stats: null
          }
        }
      })
      
      const buildingStatsResults = await Promise.all(buildingStatsPromises)
      setBuildingStats(buildingStatsResults)
    } catch (error) {
      console.error('Error loading buildings:', error)
    }
  }

  const loadBuildingStats = async (buildingId) => {
    try {
      setBuildingStatsLoading(true)
      const response = await announcementApi.getBuildingStats(buildingId)
      setSelectedBuildingStats(response.data)
    } catch (error) {
      console.error('Error loading building stats:', error)
      setSelectedBuildingStats(null)
    } finally {
      setBuildingStatsLoading(false)
    }
  }

  const StatCard = ({ title, value, icon: Icon, color = 'indigo', subtext, trend }) => (
    <div className="bg-white overflow-hidden shadow rounded-lg">
      <div className="p-5">
        <div className="flex items-center">
          <div className="flex-shrink-0">
            <Icon className={`h-6 w-6 text-${color}-600`} />
          </div>
          <div className="ml-5 w-0 flex-1">
            <dl>
              <dt className="text-sm font-medium text-gray-500 truncate">{title}</dt>
              <dd className={`text-lg font-medium text-${color}-900`}>
                {value !== null && value !== undefined ? value.toLocaleString() : 'N/A'}
              </dd>
              {subtext && (
                <dd className="text-sm text-gray-500">{subtext}</dd>
              )}
              {trend && (
                <dd className={`text-sm ${trend.positive ? 'text-green-600' : 'text-red-600'} flex items-center`}>
                  <TrendingUp className="h-4 w-4 mr-1" />
                  {trend.value}
                </dd>
              )}
            </dl>
          </div>
        </div>
      </div>
    </div>
  )

  const NotificationMethodChart = ({ data, title }) => {
    if (!data || typeof data !== 'object') return null

    const methods = Object.entries(data)
    const total = methods.reduce((sum, [, count]) => sum + count, 0)
    
    if (total === 0) return null

    return (
      <div className="bg-white shadow rounded-lg p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">{title}</h3>
        <div className="space-y-4">
          {methods.map(([method, count]) => {
            const percentage = total > 0 ? (count / total) * 100 : 0
            const getIcon = () => {
              switch (method.toLowerCase()) {
                case 'email': return Mail
                case 'sms': return MessageCircle
                case 'pushnotification': return Bell
                default: return Bell
              }
            }
            const Icon = getIcon()

            return (
              <div key={method} className="flex items-center">
                <Icon className="h-5 w-5 text-gray-400 mr-3" />
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-medium text-gray-900">{method}</span>
                    <span className="text-sm text-gray-500">{count}</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-indigo-600 h-2 rounded-full"
                      style={{ width: `${percentage}%` }}
                    />
                  </div>
                  <div className="text-xs text-gray-500 mt-1">{percentage.toFixed(1)}%</div>
                </div>
              </div>
            )
          })}
        </div>
      </div>
    )
  }

  if (loading) {
    return <LoadingSpinner />
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Announcement Statistics"
        subtitle="Overview of announcement performance and engagement"
        action={
          <div className="flex items-center space-x-4">
            <BarChart3 className="h-5 w-5 text-gray-400" />
            <span className="text-sm text-gray-500">Real-time data</span>
          </div>
        }
      />

      {/* Global Statistics */}
      {globalStats && (
        <div>
          <h2 className="text-lg font-medium text-gray-900 mb-4">Global Statistics</h2>
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            <StatCard
              title="Total Announcements"
              value={globalStats.totalAnnouncements}
              icon={MessageSquare}
              color="blue"
            />
            <StatCard
              title="Total Recipients"
              value={globalStats.totalRecipients}
              icon={Users}
              color="green"
            />
            <StatCard
              title="Total Read"
              value={globalStats.totalRead}
              icon={CheckCircle}
              color="indigo"
              subtext={globalStats.totalRecipients > 0 ? 
                `${((globalStats.totalRead / globalStats.totalRecipients) * 100).toFixed(1)}% read rate` : 
                undefined
              }
            />
            <StatCard
              title="Pending Acknowledgments"
              value={globalStats.pendingAcknowledgments}
              icon={AlertCircle}
              color="yellow"
            />
          </div>
          
          {globalStats.notificationMethodCounts && (
            <div className="mt-6">
              <NotificationMethodChart 
                data={globalStats.notificationMethodCounts} 
                title="Global Notification Method Distribution"
              />
            </div>
          )}
        </div>
      )}

      {/* Building Selection */}
      <div className="bg-white shadow rounded-lg p-6">
        <h2 className="text-lg font-medium text-gray-900 mb-4">Building-Specific Statistics</h2>
        <div className="flex items-center space-x-4">
          <label htmlFor="building-select" className="text-sm font-medium text-gray-700">
            Select Building:
          </label>
          <select
            id="building-select"
            value={selectedBuildingId}
            onChange={(e) => setSelectedBuildingId(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
          >
            <option value="">All Buildings Overview</option>
            {buildings.map((building) => (
              <option key={building.buildingId} value={building.buildingId}>
                {building.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Selected Building Stats */}
      {selectedBuildingId && (
        <div>
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            {buildings.find(b => b.buildingId === selectedBuildingId)?.name} Statistics
          </h3>
          {buildingStatsLoading ? (
            <LoadingSpinner />
          ) : selectedBuildingStats ? (
            <div>
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
                <StatCard
                  title="Total Announcements"
                  value={selectedBuildingStats.totalAnnouncements}
                  icon={MessageSquare}
                  color="blue"
                />
                <StatCard
                  title="Total Recipients"
                  value={selectedBuildingStats.totalRecipients}
                  icon={Users}
                  color="green"
                />
                <StatCard
                  title="Total Read"
                  value={selectedBuildingStats.totalRead}
                  icon={CheckCircle}
                  color="indigo"
                  subtext={selectedBuildingStats.totalRecipients > 0 ? 
                    `${((selectedBuildingStats.totalRead / selectedBuildingStats.totalRecipients) * 100).toFixed(1)}% read rate` : 
                    undefined
                  }
                />
                <StatCard
                  title="Pending Acknowledgments"
                  value={selectedBuildingStats.pendingAcknowledgments}
                  icon={AlertCircle}
                  color="yellow"
                />
              </div>
              
              {selectedBuildingStats.notificationMethodCounts && (
                <div className="mt-6">
                  <NotificationMethodChart 
                    data={selectedBuildingStats.notificationMethodCounts} 
                    title={`${buildings.find(b => b.buildingId === selectedBuildingId)?.name} Notification Methods`}
                  />
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-8">
              <MessageSquare className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">No data available</h3>
              <p className="mt-1 text-sm text-gray-500">Statistics for this building are not available.</p>
            </div>
          )}
        </div>
      )}

      {/* Buildings Overview */}
      {!selectedBuildingId && buildingStats.length > 0 && (
        <div>
          <h3 className="text-lg font-medium text-gray-900 mb-4">Buildings Overview</h3>
          <div className="bg-white shadow overflow-hidden sm:rounded-md">
            <ul className="divide-y divide-gray-200">
              {buildingStats.map((building) => (
                <li key={building.buildingId}>
                  <div className="px-4 py-4 flex items-center justify-between">
                    <div className="flex items-center">
                      <div className="flex-shrink-0">
                        <div className="h-10 w-10 rounded-full bg-indigo-100 flex items-center justify-center">
                          <MessageSquare className="h-5 w-5 text-indigo-600" />
                        </div>
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">
                          {building.buildingName}
                        </div>
                        <div className="text-sm text-gray-500">
                          {building.stats ? 
                            `${building.stats.totalAnnouncements || 0} announcements` : 
                            'No data'
                          }
                        </div>
                      </div>
                    </div>
                    
                    {building.stats && (
                      <div className="flex items-center space-x-4">
                        <div className="text-center">
                          <div className="text-sm font-medium text-gray-900">
                            {building.stats.totalRecipients || 0}
                          </div>
                          <div className="text-xs text-gray-500">Recipients</div>
                        </div>
                        <div className="text-center">
                          <div className="text-sm font-medium text-green-600">
                            {building.stats.totalRead || 0}
                          </div>
                          <div className="text-xs text-gray-500">Read</div>
                        </div>
                        <div className="text-center">
                          <div className="text-sm font-medium text-yellow-600">
                            {building.stats.pendingAcknowledgments || 0}
                          </div>
                          <div className="text-xs text-gray-500">Pending</div>
                        </div>
                        <button
                          onClick={() => setSelectedBuildingId(building.buildingId)}
                          className="text-indigo-600 hover:text-indigo-900 text-sm font-medium"
                        >
                          View Details
                        </button>
                      </div>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  )
}

export default AnnouncementStats